import React, { useEffect, useState } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { motion } from 'framer-motion';
import HeroImage from '../../assets/Systemische Beratung in Dresden - Madlin Firme malneudenken.jpg';

const HERO_IMAGE = {
  maxWidth: 500,
  radius: 'var(--radius-xl)',
  shadow: true,
  fit: 'cover',
};

const HeroSection = () => {
  const { features } = useTheme();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    if (!features.parallax) return;
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [features.parallax]);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: features.smoothScroll ? 'smooth' : 'auto' });
  };

  return (
    <section
	  id="home"
	  className="relative min-h-screen flex items-center overflow-hidden pt-24 sm:pt-20 lg:pt-0"
	  style={{ backgroundColor: 'var(--color-bg-primary)' }}
	>
      {/* Subtiler Hintergrund-Glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full blur-3xl opacity-10"
          style={{
            backgroundColor: 'var(--color-secondary-200)',
            transform: `translateY(${features.parallax ? scrollY * 0.05 : 0}px)`,
          }}
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">

          {/* TEXT */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
            className="text-center lg:text-left"
          >
            {/* SEO: H1 enthält Haupt-Keywords */}
            <p
              className="inline-flex items-center gap-2 mb-5"
              style={{ color: 'var(--color-text-muted)', fontSize: 14 }}
            >
              <span style={{ width: 8, height: 8, borderRadius: 999, backgroundColor: 'var(--color-accent-500)' }} />
              Systemische Beratung · Dresden & Online
            </p>

            <h1
              className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Gemeinsam neue{' '}
              <span style={{ boxShadow: 'inset 0 -0.22em 0 rgba(212,126,82,0.28)', padding: '0 0.08em' }}>
                Perspektiven
              </span>{' '}
              entdecken
            </h1>

            {/* SEO: Beschreibung mit Keywords Trauma, Burnout, Trennung, Dresden */}
            <p
              className="mt-6 text-lg md:text-xl leading-relaxed"
              style={{ color: 'var(--color-text-secondary)', maxWidth: 620 }}
            >
              In herausfordernden Lebensphasen begleite ich Sie mit systemischem Blick & Feingefühl zu mehr Klarheit & innerer Stärke.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a
                href="#contact"
                className="btn btn-accent"
                style={{ borderRadius: 'var(--radius-pill)' }}
                onClick={scrollTo('#contact')}
              >
                Erstgespräch vereinbaren
              </a>
              <a
                href="#about"
                className="btn btn-ghost"
                style={{ borderRadius: 'var(--radius-pill)' }}
                onClick={scrollTo('#about')}
              >
                Mehr erfahren →
              </a>
            </div>

            {/* SEO: Trust-Signale mit Keywords */}
            <div
              className="mt-10 flex flex-wrap gap-x-6 gap-y-3 justify-center lg:justify-start"
              style={{ color: 'var(--color-text-muted)', fontSize: 14 }}
            >
              {[
                'Zertifizierte Beraterin (SG)',
                'Staatl. anerkannte Sozialarbeiterin',
                'zugewandt & wertschätzend',
              ].map((t) => (
                <span key={t} className="inline-flex items-center gap-2">
                  <span style={{ width: 6, height: 6, borderRadius: 999, backgroundColor: 'var(--color-primary-500)' }} />
                  {t}
                </span>
              ))}
            </div>
          </motion.div>

          {/* HERO IMAGE */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.65, delay: 0.1 }}
            className="relative"
          >
            <img
              src={HeroImage}
              alt="Madlin Firme – Systemische Beraterin in Dresden"
              className="w-full max-w-[90vw] sm:max-w-none h-auto mx-auto"
              style={{
                maxWidth: HERO_IMAGE.maxWidth,
                borderRadius: HERO_IMAGE.radius,
                objectFit: HERO_IMAGE.fit,
                boxShadow: HERO_IMAGE.shadow ? 'var(--shadow-sm)' : 'none',
              }}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
